import classNames from "classnames";
import React from "react";

import { ITestable, testAttr, TEST_PROP } from "@/browser";

import { Icon } from "../Icon";

export type TIconButtonVariant = "primary" | "secondary" | "info" | "success" | "danger" | "warning" | "link";
export type TIconButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

/**
 * Base <button /> element properties
 */
type TButtonHTMLProps = Omit<JSX.IntrinsicElements["button"], "color">;

/**
 * The IconButton component properties interface
 */
export interface IIconButtonProps<TVariant = TIconButtonVariant, TSize = TIconButtonSize>
  extends ITestable,
    TButtonHTMLProps {
  /**
   * Determines the icon displayed inside of the icon button
   */
  icon?: string;

  /**
   * The variant of the icon button
   * @default clean
   */
  variant?: TVariant;

  /**
   * The size of the icon button
   * @default md
   */
  size?: TSize;

  /**
   * While is set up to true the `icon-button--active` class is added which indicates that this element is active
   * @default false
   */
  active?: boolean;
}

export function IconButton<TVariant extends string = TIconButtonVariant, TSize extends string = TIconButtonSize>(
  props: IIconButtonProps<TIconButtonVariant | TVariant, TIconButtonSize | TSize>,
) {
  const {
    icon,
    variant = "primary",
    size = "md",
    active = false,
    type = "button",
    className,
    [TEST_PROP]: testProp,
    children,
    ...rest
  } = props;
  const testableProps = testAttr({ [TEST_PROP]: testProp, ...rest }, { sufix: "icon-button" });
  const variantClass = `icon-button--variant-${variant}`;
  const sizeClass = `icon-button--size-${size}`;
  const classes = classNames(
    "icon-button",
    variantClass,
    sizeClass,
    {
      "icon-button--active": active,
    },
    className,
  );

  return (
    <button type={type} className={classes} {...testableProps} {...rest}>
      {icon && <Icon icon={icon} size={size} />}
      {children}
    </button>
  );
}
